import axios from 'axios'

/**
 * Binary-safe download of a Kiirhindamise lühiaruanne .docx.
 *
 * Uses axios (not Inertia's router) because Inertia's response pipeline is
 * JSON-only and throws on binary payloads.
 *
 * Wire format (RED-210 payload diet): multipart/form-data instead of base64
 * JSON. Previously shipped up to 72 MB of base64 data URIs; now ships raw
 * Blobs. Saves the 33% base64 bloat on the way up and skips PHP-side
 * `base64_decode` + temp-file write on arrival (`QuickValuationExportService
 * ::decodeDataUriToTempFile` is no longer in the hot path).
 *
 * Returns `{ landRegistrySource }` so the caller can surface a source-aware
 * toast ("Andmed vahemälust — tasuta", "Andmed värskelt päritud ~6€",
 * "Kinnistusraamatu päring ebaõnnestus"). `landRegistrySource` is null when
 * addLandRegistry was false.
 */
export function useShortReportDownload() {
  const download = async ({
    evaluationId,
    templateId,
    fallbackFilename = 'luhiaruanne.docx',
    maps = {},
    esg = {},
    esgSummary = null,
    transactions = null,
    addLandRegistry = false,
    useExistingLandRegistry = false,
    onUploadProgress = null,
    onDownloadProgress = null,
  }) => {
    const form = new FormData()
    form.append('template_id', String(templateId))
    form.append('add_land_registry', addLandRegistry ? '1' : '0')
    form.append('use_existing_land_registry', useExistingLandRegistry ? '1' : '0')

    // Complex nested objects ride along as JSON strings — multipart doesn't
    // model nested objects natively and the Laravel side decodes them back.
    if (esg && Object.keys(esg).length > 0) {
      form.append('esg_json', JSON.stringify(esg))
    }
    if (Array.isArray(esgSummary)) {
      form.append('esg_summary_json', JSON.stringify(esgSummary))
    }
    if (transactions) {
      form.append('transactions_json', JSON.stringify(transactions))
    }

    await appendMapsToFormData(form, maps)

    // Forward the `?profile=1` flag so the server logs its half of the chain.
    const profileFlag = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).has('profile')
      ? '?profile=1'
      : ''
    const response = await axios.post(
      `/quick-valuation/${evaluationId}/export${profileFlag}`,
      form,
      {
        responseType: 'blob',
        // Let the browser set Content-Type with multipart boundary.
        ...(typeof onUploadProgress === 'function' ? { onUploadProgress } : {}),
        ...(typeof onDownloadProgress === 'function' ? { onDownloadProgress } : {}),
      },
    )

    const filename = extractFilename(response.headers?.['content-disposition']) || fallbackFilename
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return {
      landRegistrySource: response.headers?.['x-kinnistusraamat-source'] ?? null,
    }
  }

  /**
   * RED-212: warm-capture upload. Persists captured maps server-side so a
   * later export from the QV history view (where QvHiddenMaps isn't mounted)
   * gets the same images as a fresh result-page export.
   *
   * Fire-and-forget from the caller's perspective — failures are swallowed
   * here and surfaced only as a console warning, since the user-facing UX
   * doesn't depend on this succeeding.
   */
  const saveMaps = async ({ evaluationId, maps }) => {
    if (!evaluationId || !maps) return false

    const form = new FormData()
    const appended = await appendMapsToFormData(form, maps)
    if (appended === 0) return false

    try {
      await axios.post(`/quick-valuation/${evaluationId}/maps`, form)
      return true
    } catch (err) {
      console.warn('[useShortReportDownload] saveMaps failed', err)
      return false
    }
  }

  return { download, saveMaps }
}

/**
 * Append the captured `maps` payload (top-level mikro/makro/kataster/infra
 * plus nested `esg`) onto a FormData. Returns the number of blobs actually
 * appended so callers can short-circuit when nothing was captured.
 */
async function appendMapsToFormData(form, maps) {
  let count = 0

  for (const key of ['mikro', 'makro', 'kataster', 'infra']) {
    const dataUri = maps?.[key]
    const blob = await dataUriToBlob(dataUri)
    if (blob) {
      form.append(`maps[${key}]`, blob, filenameFor(key, blob.type))
      count++
    }
  }

  const esgMaps = maps?.esg || {}
  for (const [id, dataUri] of Object.entries(esgMaps)) {
    const blob = await dataUriToBlob(dataUri)
    if (blob) {
      form.append(`maps[esg][${id}]`, blob, filenameFor(`esg-${id}`, blob.type))
      count++
    }
  }

  return count
}

/**
 * Convert a `data:image/png;base64,...` URI produced by `canvas.toDataURL()`
 * into a Blob suitable for multipart upload. Returns null for empty/invalid
 * inputs so the caller can skip appending anything (keeps the POST small when
 * a map capture failed).
 *
 * Decodes base64 directly rather than `fetch(dataUri)` — data URIs aren't
 * network resources, and treating them as such trips the page's CSP
 * `connect-src` directive (no `data:` source allowed, and rightly so).
 * Direct decode is also faster: no pseudo-fetch round-trip, no Response wrapper.
 */
function dataUriToBlob(dataUri) {
  if (typeof dataUri !== 'string' || !dataUri.startsWith('data:')) {
    return null
  }
  try {
    const commaIdx = dataUri.indexOf(',')
    if (commaIdx < 0) return null

    const header = dataUri.slice(5, commaIdx) // strip 'data:' prefix
    const isBase64 = header.endsWith(';base64')
    const mime = (isBase64 ? header.slice(0, -7) : header) || 'application/octet-stream'
    const payload = dataUri.slice(commaIdx + 1)

    let bytes
    if (isBase64) {
      const binary = atob(payload)
      bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
      }
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(payload))
    }

    return new Blob([bytes], { type: mime })
  } catch (err) {
    console.warn('[useShortReportDownload] dataUriToBlob failed', err)
    return null
  }
}

function filenameFor(key, mime) {
  const ext = mime === 'image/jpeg' ? 'jpg' : 'png'
  return `${key}.${ext}`
}

function extractFilename(contentDisposition) {
  if (!contentDisposition) return null
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match) return decodeURIComponent(utf8Match[1])
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
  return plainMatch ? plainMatch[1] : null
}
